import { useState, useMemo, useCallback } from 'react';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { useProcedureCatalog, ProcedureRecord } from '@/hooks/useProcedureBundles';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink,
  PaginationNext, PaginationPrevious,
} from '@/components/ui/pagination';
import { Search, Loader2, Package, Filter, Pencil, Trash2, Save, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const ROWS_PER_PAGE = 25;

export default function ProcedureCatalogPage() {
  const { procedures, loading, setProcedures } = useProcedureCatalog();
  const [search, setSearch] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('all');
  const [page, setPage] = useState(1);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<ProcedureRecord>>({});

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<ProcedureRecord | null>(null);

  const specialties = useMemo(
    () => [...new Set(procedures.map(p => p.specialty))].sort(),
    [procedures]
  );

  const filtered = useMemo(() => {
    return procedures.filter(p => {
      const matchesSearch =
        !search ||
        p.cpt_code.toLowerCase().includes(search.toLowerCase()) ||
        p.name.toLowerCase().includes(search.toLowerCase());
      const matchesSpecialty = specialtyFilter === 'all' || p.specialty === specialtyFilter;
      return matchesSearch && matchesSpecialty;
    });
  }, [procedures, search, specialtyFilter]);

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE) || 1;
  const paged = useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE;
    return filtered.slice(start, start + ROWS_PER_PAGE);
  }, [filtered, page]);

  const startEdit = (p: ProcedureRecord) => {
    setEditingId(p.id);
    setEditForm({ name: p.name, specialty: p.specialty, billable_type: p.billable_type, active: p.active });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = useCallback(async () => {
    if (!editingId) return;
    const { error } = await supabase
      .from('procedures')
      .update({
        name: editForm.name,
        specialty: editForm.specialty,
        billable_type: editForm.billable_type,
        active: editForm.active,
      })
      .eq('id', editingId);

    if (error) {
      toast({ title: 'Update failed', description: error.message, variant: 'destructive' });
      return;
    }

    setProcedures(prev =>
      prev.map(p => p.id === editingId ? { ...p, ...editForm } as ProcedureRecord : p)
    );
    setEditingId(null);
    setEditForm({});
    toast({ title: 'Procedure updated' });
  }, [editingId, editForm, setProcedures]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const { error } = await supabase
      .from('procedures')
      .delete()
      .eq('id', deleteTarget.id);

    if (error) {
      toast({ title: 'Delete failed', description: error.message, variant: 'destructive' });
    } else {
      setProcedures(prev => prev.filter(p => p.id !== deleteTarget.id));
      toast({ title: 'Procedure deleted', description: `${deleteTarget.cpt_code} removed.` });
    }
    setDeleteTarget(null);
  }, [deleteTarget, setProcedures]);

  return (
    <OpsLayout>
      <div className="p-6 space-y-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Package className="w-6 h-6 text-primary" />
              Procedure Catalog
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {procedures.length} procedures — click <Pencil className="w-3 h-3 inline" /> to edit any record
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search CPT or name..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-10"
                />
              </div>
              <Select value={specialtyFilter} onValueChange={v => { setSpecialtyFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[200px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Specialties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Specialties</SelectItem>
                  {specialties.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">CPT</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Specialty</TableHead>
                      <TableHead className="w-[120px]">Type</TableHead>
                      <TableHead className="w-[80px]">Active</TableHead>
                      <TableHead className="w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paged.map(p => {
                      const isEditing = editingId === p.id;
                      return (
                        <TableRow key={p.id}>
                          <TableCell className="font-mono font-semibold">{p.cpt_code}</TableCell>
                          <TableCell>
                            {isEditing ? (
                              <Input
                                value={editForm.name || ''}
                                onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                                className="h-8 text-sm"
                              />
                            ) : p.name}
                          </TableCell>
                          <TableCell>
                            {isEditing ? (
                              <Input
                                value={editForm.specialty || ''}
                                onChange={e => setEditForm(f => ({ ...f, specialty: e.target.value }))}
                                className="h-8 text-sm"
                              />
                            ) : (
                              <Badge variant="secondary" className="text-xs">{p.specialty}</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditing ? (
                              <Select
                                value={editForm.billable_type}
                                onValueChange={v => setEditForm(f => ({ ...f, billable_type: v as any }))}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="BILLABLE">Billable</SelectItem>
                                  <SelectItem value="DISPLAY_ONLY">Display Only</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge
                                variant={p.billable_type === 'DISPLAY_ONLY' ? 'outline' : 'default'}
                                className={
                                  p.billable_type === 'DISPLAY_ONLY'
                                    ? 'text-muted-foreground border-muted-foreground/30'
                                    : ''
                                }
                              >
                                {p.billable_type === 'DISPLAY_ONLY' ? 'Display Only' : 'Billable'}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditing ? (
                              <Switch
                                checked={editForm.active}
                                onCheckedChange={v => setEditForm(f => ({ ...f, active: v }))}
                              />
                            ) : (
                              <div className={cn('w-2.5 h-2.5 rounded-full', p.active ? 'bg-emerald-500' : 'bg-muted')} />
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditing ? (
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-primary" onClick={saveEdit}>
                                  <Save className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={cancelEdit}>
                                  <X className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => startEdit(p)}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => setDeleteTarget(p)}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          No procedures found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {totalPages > 1 && (
                  <div className="p-4 border-t">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => setPage(Math.max(1, page - 1))}
                            className={cn(page === 1 && 'pointer-events-none opacity-50', 'cursor-pointer')}
                          />
                        </PaginationItem>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                          .map((p, i, arr) => (
                            <PaginationItem key={p}>
                              {i > 0 && arr[i - 1] !== p - 1 && <span className="px-1 text-muted-foreground">…</span>}
                              <PaginationLink isActive={p === page} onClick={() => setPage(p)} className="cursor-pointer">
                                {p}
                              </PaginationLink>
                            </PaginationItem>
                          ))}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() => setPage(Math.min(totalPages, page + 1))}
                            className={cn(page === totalPages && 'pointer-events-none opacity-50', 'cursor-pointer')}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Procedure</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.cpt_code}</strong> — {deleteTarget?.name}?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </OpsLayout>
  );
}
